// Shared mobile download platform configuration and utilities
// Used by T-Mobile, Metro, and other mobile partners

export const PlatformMap = {
  android: {
    id: 'android',
    name: 'Android',
    platformCode: 0,
    userAgentPatterns: [/Android/i],
  },
  iphone: {
    id: 'iphone',
    name: 'iPhone',
    platformCode: 5,
    userAgentPatterns: [/iPhone/i, /iPad/i],
  },
};

/**
 * Detect platform from User-Agent string
 * @returns {string|null} Platform ID or null if not detected
 */
export const detectPlatform = () => {
  const userAgent = navigator.userAgent;

  for (const [platformId, platformInfo] of Object.entries(PlatformMap)) {
    for (const pattern of platformInfo.userAgentPatterns) {
      if (pattern.test(userAgent)) {
        return platformId;
      }
    }
  }

  return null;
};

/**
 * Get supported platforms from environment variable
 * @param {string} envVarName - Environment variable name containing supported platforms
 * @returns {string[]} Array of supported platform IDs
 */
export const getSupportedPlatforms = (envVarName) => {
  const supportedStr = process.env[envVarName] || 'android,iphone';
  return supportedStr.split(',').map(p => p.trim()).filter(p => p in PlatformMap);
};

/**
 * Get download URL for a specific platform from partner-specific environment variables
 * @param {string} platformId - Platform ID
 * @param {object} urlConfig - Object with partner-specific URLs { android: URL, iphone: URL }
 * @returns {string|null} Download URL or null if not available
 */
export const getDownloadUrl = (platformId, urlConfig) => {
  if (!urlConfig[platformId]) {
    return null;
  }

  return urlConfig[platformId];
};

/**
 * Get platform display name
 * @param {string} platformId - Platform ID
 * @returns {string} Display name
 */
export const getPlatformName = (platformId) => {
  return PlatformMap[platformId]?.name || platformId;
};

/**
 * Track analytics event for a partner
 * @param {string} eventName - Event name
 * @param {object} config - Partner config with affiliateId, brandingId, analyticsId
 * @param {object} eventData - Event data
 */
export const trackEvent = (eventName, config, eventData = {}) => {
  const analyticsData = {
    ...eventData,
    affiliateId: config.affiliateId,
    brandingId: config.brandingId,
    timestamp: new Date().toISOString(),
  };

  // Log to console in development
  // Development logging removed

  // Send to Google Analytics if available
  if (window.gtag && config.analyticsId) {
    window.gtag('event', eventName, analyticsData);
  }
};
